import React, { useState, useEffect, useRef } from 'react';
import LeftDashboard from '../components/LeftDashboard';
import StatsBar from '../components/StatsBar';
import ReviewCard from '../components/ReviewCard';
import LiveFeed from '../components/LiveFeed';

// Inline page components (embedded mode)
import JobsPage from './JobsPage';
import CompaniesPage from './CompaniesPage';
import CompanyReviewsPage from './CompanyReviewsPage';
import MostLikedPage from './MostLikedPage';
import SalariesPage from './SalariesPage';
import CompanyDetailPage from './CompanyDetailPage';
import TopQuestionsPage from './TopQuestionsPage';
import SavedKahanisPage from './SavedKahanisPage';
import QuestionReviewsPage from './QuestionReviewsPage';
import CompanyHeatMapPage from './CompanyHeatMapPage';
import MyKahaniPage from './MyKahaniPage';
import SimilarKahanisPage from './SimilarKahanisPage';

import Loader from '../components/Loader';
import { fetchCompanies } from '../api';

const FILTERS = ['All', '⏰ Recent', '👻 Ghosted', '✅ Received', '❌ Rejected', '⏳ In Progress'];

// ── Search Results View ──────────────────────────────────────────────────────
function SearchResultsView({ query, reviews, loading, setView, setSelectedCompany, activeFilter, setActiveFilter, user }) {
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    if (!query) return;
    fetchCompanies({ limit: 50 })
      .then(data => {
        const q = query.toLowerCase();
        const matched = (data.companies || []).filter(c =>
          c.name?.toLowerCase().includes(q)
        );
        setCompanies(matched.slice(0, 5));
      })
      .catch(() => setCompanies([]));
  }, [query]);

  const totalResults = reviews.length + companies.length;

  return (
    <div style={{ animation: 'fadeUp 0.3s ease-out' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: 'var(--display)', fontSize: 22, fontWeight: 900, color: 'var(--text)', letterSpacing: -0.5 }}>
          Search Results
        </div>
        <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 4 }}>
          {loading ? 'Searching…' : `${totalResults} result${totalResults !== 1 ? 's' : ''} for `}
          {!loading && <span style={{ color: 'var(--blue)', fontWeight: 600 }}>"{query}"</span>}
        </div>
      </div>

      {/* Filter Chips — shown for search results */}
      <div
        className="filter-chips-row"
        style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}
      >
        {FILTERS.map(f => (
          <button
            key={f}
            onClick={() => setActiveFilter(f)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '6px 14px', borderRadius: 100, fontSize: 12,
              border: `1px solid ${activeFilter === f ? 'var(--blue)' : 'var(--border2)'}`,
              background: activeFilter === f ? 'var(--blue)' : 'var(--surface)',
              color: activeFilter === f ? '#fff' : 'var(--text2)',
              cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
              fontFamily: 'var(--sans)', fontWeight: activeFilter === f ? 600 : 500,
              flexShrink: 0,
            }}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Matching Companies */}
      {companies.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 10 }}>
            Companies
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {companies.map((c, i) => (
              <div
                key={c.id || i}
                onClick={() => { setSelectedCompany(c.name, 'All'); setView('company-reviews'); }}
                style={{
                  background: '#fff', border: '1px solid var(--border)', borderRadius: 12,
                  padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 14,
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--blue)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{
                  width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                  background: 'linear-gradient(135deg, var(--blue), #0284c7)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, color: '#fff', fontWeight: 900,
                }}>
                  {c.name?.charAt(0)?.toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 1 }}>
                    {c.totalReviews || 0} reports · 👻 {c.ghostPercent || 0}% ghost · ✅ {c.offerRate || 0}% offer
                  </div>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2.5">
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Matching Kahanis */}
      <div>
        {companies.length > 0 && (
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 10 }}>
            Kahanis
          </div>
        )}
        {loading ? (
          <Loader inline message="Searching kahanis…" />
        ) : reviews.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text3)', fontSize: 14 }}>
            {companies.length === 0 ? `No results found for "${query}".` : 'No matching reviews.'}
          </div>
        ) : (
          reviews.map((r, i) => (
            <ReviewCard key={r._id || r.id || i} review={r} delay={i * 0.04} currentUser={user} onSignInRequired={() => setView('login')} />
          ))
        )}
      </div>
    </div>
  );
}


const GUEST_RESTRICTED = ['top-questions', 'company-heatmap', 'most-liked', 'salaries'];

export default function Home({ reviews, loading, activeFilter, setActiveFilter, searchQuery, onAddClick, onSearch, user, view, setView, onHomeClick, selectedCompany, setSelectedCompany, onEditClick, onDeleteClick, companyFilter, setCompanyFilter, totalKahanis, selectedRole, setSelectedRole, widgetRefreshKey, lastSharedReview, onReviewClick, autoOpenComments }) {
  const [activeStat, setActiveStat] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [similarSource, setSimilarSource] = useState(null);
  const [showGuestPopup, setShowGuestPopup] = useState(false);
  const [locationFilter, setLocationFilter] = useState('');
  const [companyReviewsBackView, setCompanyReviewsBackView] = useState('companies');
  const prevViewRef = useRef(view);

  useEffect(() => {
    if (view === 'company-reviews') {
      setCompanyReviewsBackView(prevViewRef.current || 'companies');
    }
    prevViewRef.current = view;
  }, [view]);

  const isGuest = user?.isAnonymous || user?.provider === 'anon';

  // Intercept restricted views for guests — show popup and revert to home
  useEffect(() => {
    if (isGuest && GUEST_RESTRICTED.includes(view)) {
      setShowGuestPopup(true);
      onHomeClick(); // revert to home feed
    }
  }, [view, isGuest, onHomeClick]);

  // Reset StatsBar when clicking anywhere on the page, but ignore clicks on interactive items
  useEffect(() => {
    const handleClick = (e) => {
      // Don't reset if clicking a button, input, or inside specific sections
      if (e.target.closest('button') || e.target.closest('input') || e.target.closest('textarea') || e.target.closest('.comments-section') || e.target.closest('.stats-bar-wrapper')) {
        return;
      }
      setActiveStat(null);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);


  // Render home feed (no filter chips — StatsBar handles filtering)
  const renderDefaultContent = () => (
    <div data-tour="feed">
      {/* Section heading */}
      <div
        className="section-heading-row"
        style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}
      >
        <span style={{
          fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
          letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text3)', whiteSpace: 'nowrap',
        }}>
          {activeStat === 'offer-rate' ? 'Offer Received' :
           activeStat === 'ghost-rate' ? 'Ghosted Kahanis' :
           activeStat === 'rejected'   ? 'Rejected Kahanis' :
           activeStat === 'in-progress'? 'In Progress' :
           'Recent Kahanis'}
        </span>
        <div style={{ flex: 1, height: 2, borderRadius: 2, background:
          activeStat === 'offer-rate'  ? '#10b981' :
          activeStat === 'ghost-rate'  ? '#f97316' :
          activeStat === 'rejected'    ? '#991b1b' :
          activeStat === 'in-progress' ? '#eab308' :
          'var(--border)'
        }} />
        {activeStat && (
          <button
            onClick={() => { setActiveStat(null); setActiveFilter('All'); }}
            style={{
              background: 'none', border: '1px solid var(--border2)',
              borderRadius: 20, padding: '3px 10px',
              fontSize: 11, fontWeight: 600, color: 'var(--text3)',
              cursor: 'pointer', fontFamily: 'var(--sans)',
              whiteSpace: 'nowrap',
            }}
          >
            Clear ×
          </button>
        )}
      </div>

      {/* Cards */}
      {loading ? (
        <Loader inline message="Fetching kahanis..." />
      ) : reviews.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)', fontSize: 14 }}>
          No reports found. Be the first to contribute! 🎯
        </div>
      ) : (
        reviews.map((r, i) => (
          <ReviewCard key={r._id || r.id} review={r} delay={i * 0.05} currentUser={user} onSignInRequired={() => setView('login')} />
        ))
      )}
    </div>
  );

  // Render the appropriate main content based on view
  const renderViewContent = () => {
    switch (view) {
      case 'jobs':
        return <JobsPage />;
      case 'most-liked':
        return <MostLikedPage setView={setView} setActiveFilter={setActiveFilter} />;
      case 'search':
        return <SearchResultsView query={searchQuery} reviews={reviews} loading={loading} setView={setView} setSelectedCompany={setSelectedCompany} activeFilter={activeFilter} setActiveFilter={setActiveFilter} user={user} />;
      case 'companies':
      case 'companies-list':
        return <CompaniesPage setView={setView} setSelectedCompany={setSelectedCompany} activeFilter={activeFilter} setActiveFilter={setActiveFilter} totalKahanis={totalKahanis} setSelectedRole={setSelectedRole} />;
      case 'company-reviews':
        return <CompanyReviewsPage companyName={selectedCompany} roleName={selectedRole} locationName={locationFilter} setView={setView} setSelectedRole={setSelectedRole} user={user} backView={companyReviewsBackView} />;
      case 'salaries':
        return <SalariesPage setView={setView} setSelectedCompany={setSelectedCompany} setSelectedRole={setSelectedRole} onLocationNavigate={setLocationFilter} />;
      case 'company':
        return <CompanyDetailPage companyName={selectedCompany} initialFilter={companyFilter} onFilterChange={setCompanyFilter} />;
      case 'highlight':
        return (
          <div style={{ animation: 'fadeUp 0.4s ease-out' }}>
            {loading ? (
              <Loader inline message="Opening kahani..." />
            ) : reviews.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)', fontSize: 14 }}>
                Review not found. It might have been deleted. 👻
              </div>
            ) : (
              reviews.map((r, i) => (
                <ReviewCard key={r._id || r.id} review={r} delay={0.1} currentUser={user} onSignInRequired={() => setView('login')} autoOpenComments={autoOpenComments} />
              ))
            )}
            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <button 
                onClick={onHomeClick}
                style={{
                  background: 'var(--surface2)', color: 'var(--text)', border: '1px solid var(--border)',
                  padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  fontFamily: 'var(--sans)', transition: 'all 0.15s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--surface2)'}
              >
                ← Back to Community Feed
              </button>
            </div>
          </div>
        );
      case 'company-heatmap':
        return <CompanyHeatMapPage setView={setView} setSelectedCompany={setSelectedCompany} setSelectedRole={setSelectedRole} />;
      case 'top-questions':
        return <TopQuestionsPage setView={setView} setSelectedQuestion={setSelectedQuestion} />;
      case 'question-reviews':
        return <QuestionReviewsPage question={selectedQuestion} user={user} setView={setView} />;
      case 'saved-kahanis':
        return <SavedKahanisPage user={user} />;
      case 'similar-kahanis':
        return <SimilarKahanisPage review={similarSource || lastSharedReview} user={user} setView={setView} />;
      case 'my-reviews':
        return (
          <div style={{ animation: 'fadeUp 0.4s ease-out' }}>
            <div style={{
              background: 'linear-gradient(135deg, var(--blue), #0284c7)',
              padding: '16px 24px', borderRadius: 12, color: '#fff',
              boxShadow: '0 8px 16px -4px rgba(14,165,233,0.3)',
              marginBottom: 16, position: 'relative', overflow: 'hidden'
            }}>
              <div style={{ position: 'absolute', right: -10, top: -20, fontSize: 80, opacity: 0.1 }}>📝</div>
              <h1 style={{ fontFamily: 'var(--display)', fontSize: 24, fontWeight: 900, margin: '0 0 4px' }}>My Kahanis</h1>
              <p style={{ fontSize: 13, opacity: 0.9, margin: 0 }}>Your contributed interview kahanis</p>
            </div>

            {/* Filter Chips — shown for personal kahanis as per request */}
            <div
              className="filter-chips-row"
              style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}
            >
              {FILTERS.map(f => (
                <button
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '6px 14px', borderRadius: 100, fontSize: 12,
                    border: `1px solid ${activeFilter === f ? 'var(--blue)' : 'var(--border2)'}`,
                    background: activeFilter === f ? 'var(--blue)' : 'var(--surface)',
                    color: activeFilter === f ? '#fff' : 'var(--text2)',
                    cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
                    fontFamily: 'var(--sans)', fontWeight: activeFilter === f ? 600 : 500,
                    flexShrink: 0,
                  }}
                >
                  {f}
                </button>
              ))}
            </div>
            {reviews.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)', fontSize: 14 }}>
                You haven't shared any kahanis yet. Share your interview experience! 🎯
              </div>
            ) : (
              reviews.map((r, i) => (
                <ReviewCard key={r._id || r.id} review={r} delay={i * 0.05} onEdit={() => onEditClick(r)} onDelete={() => onDeleteClick(r._id || r.id)} currentUser={user} onSignInRequired={() => setView('login')} />
              ))
            )}
          </div>
        );
      default:
        return renderDefaultContent();
    }
  };

  if (view === 'my-kahani') {
    return (
      <MyKahaniPage
        user={user}
        reviews={reviews}
        loading={loading}
        onAddClick={onAddClick}
        onEditClick={onEditClick}
        onDeleteClick={onDeleteClick}
        onSeeSimilar={(reviewOrObj) => {
          setSimilarSource(reviewOrObj || null);
          setView('similar-kahanis');
        }}
        onLogout={() => setView('login')}
        setView={setView}
        onHomeClick={onHomeClick}
      />
    );
  }

  return (
    <>
      {/* Three-column desktop grid */}
      <div
        className="home-grid"
        style={{
          maxWidth: 1440, margin: '0 auto',
          padding: '0 8px 16px 24px',
          display: 'grid',
          gap: 20,
        }}
      >
        {/* Left Dashboard (hidden on mobile via CSS) */}
        <div className="left-dashboard-col">
          <LeftDashboard
            user={user}
            view={view}
            setView={setView}
            onHomeClick={onHomeClick}
            onAddClick={onAddClick}
            isGuest={isGuest}
          />
        </div>

        {/* Main Content */}
        <main className="main-scroll-content" style={{ minWidth: 0, paddingTop: 24 }}>
          {/* StatsBar — only shown on Recent/home view */}
          {(view === 'home' || view === 'search' || view === 'highlight') && (
            <div className="stats-bar-wrapper" style={{ position: 'sticky', top: 52, zIndex: 10, background: 'var(--bg)', paddingBottom: 8, overflow: 'visible' }}>
              <div className="stats-bar-inner">
                <StatsBar
                  activeStat={activeStat}
                  setActiveStat={setActiveStat}
                  refreshSignal={widgetRefreshKey}
                  setView={setView}
                  setActiveFilter={setActiveFilter}
                />
              </div>
              {/* gradient fade below stats bar */}
              <div style={{ position: 'absolute', left: 0, right: 0, bottom: -20, height: 20, pointerEvents: 'none', background: 'linear-gradient(to bottom, var(--bg-sticky), transparent)', zIndex: 1 }} />
            </div>
          )}

          {renderViewContent()}
        </main>

        {/* Right Sidebar — Live Feed (hidden on mobile via CSS) */}
        <div className="right-sidebar-col">
          <LiveFeed onActivityClick={onReviewClick} isGuest={isGuest} onSignInRequired={() => setView('login')} />
        </div>
      </div>

      {/* ── Guest Sign-In Popup ── */}
      {showGuestPopup && (
        <div
          onClick={() => setShowGuestPopup(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 20, padding: '36px 32px',
              maxWidth: 380, width: '100%', textAlign: 'center',
              boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
              animation: 'fadeUp 0.25s ease-out',
            }}
          >
            <div style={{ fontSize: 44, marginBottom: 14 }}>🔒</div>
            <h2 style={{ fontFamily: 'var(--display)', fontSize: 20, fontWeight: 900, margin: '0 0 10px', color: 'var(--text)' }}>
              Sign in to view this page
            </h2>
            <p style={{ fontSize: 13, color: 'var(--text3)', margin: '0 0 28px', lineHeight: 1.6 }}>
              Create a free account or sign in to access Top Questions, Company Heat Map, Most Liked, and Salary Guide.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={() => { setShowGuestPopup(false); setView('login'); }}
                style={{
                  background: 'var(--blue)', color: '#fff', border: 'none',
                  padding: '13px', borderRadius: 10, fontSize: 14, fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'var(--sans)',
                  boxShadow: '0 4px 12px rgba(14,165,233,0.3)',
                }}
              >
                Sign In / Sign Up
              </button>
              <button
                onClick={() => setShowGuestPopup(false)}
                style={{
                  background: 'transparent', color: 'var(--text3)',
                  border: '1px solid var(--border)',
                  padding: '12px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'var(--sans)',
                }}
              >
                Not Now — Continue as Guest
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
